import { z } from 'zod';

/**
 * Validate Vite-exposed env at module load. A typo in a deployment env
 * fails the bundle at boot, not at the first 500 a user hits.
 *
 * RULE: never put a secret in here. Browser-bundled env values are public.
 * API keys live only in apps/api via Secret Manager (see DESIGN.md §7.4).
 *
 * Identity Platform values are *optional* until that integration lands —
 * a feature that needs them imports `requireIdentityConfig()` and gets a
 * runtime error if they were not configured. This keeps the env-validation
 * gate honest (fail-fast on real misconfig) without blocking development on
 * features not yet wired.
 */
const EnvSchema = z.object({
  VITE_API_BASE_URL: z.string().url().default('http://localhost:8080'),
  VITE_IDENTITY_API_KEY: z.string().optional(),
  VITE_IDENTITY_PROJECT_ID: z.string().optional(),
  // GlitchTip DSN — points at the self-hosted GlitchTip instance, not
  // Sentry SaaS. The DSN is browser-public (it's the ingestion endpoint).
  VITE_GLITCHTIP_DSN: z.string().url().optional(),
  VITE_ENV: z.enum(['development', 'staging', 'production']).default('development'),
});

const parsed = EnvSchema.safeParse(import.meta.env);
if (!parsed.success) {
  // eslint-disable-next-line no-console
  console.error('Invalid environment for @keobook/web:', parsed.error.flatten().fieldErrors);
  throw new Error('Environment validation failed. See console for details.');
}

export const env = parsed.data;

export function requireIdentityConfig(): { apiKey: string; projectId: string } {
  const { VITE_IDENTITY_API_KEY: apiKey, VITE_IDENTITY_PROJECT_ID: projectId } = env;
  if (!apiKey || !projectId) {
    throw new Error(
      'Identity Platform is not configured. Set VITE_IDENTITY_API_KEY and VITE_IDENTITY_PROJECT_ID in apps/web/.env.local.',
    );
  }
  return { apiKey, projectId };
}
